// LimbControl.ts: Neural control system for creature limb movement and coordination
// Integrates with the neural network to provide intelligent limb control

import { Limb, LimbSegment, LimbType } from './limbSystem';
import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { Node } from './types';
import { LifeBehaviorMathEngine, AttractorState } from './lifeBehaviorMath';

export interface ControlSignal {
    limbId: number;
    targetX: number;
    targetY: number;
    targetZ: number;
    intensity: number;
    priority: number;
    duration: number;
    timestamp: number;
}

export interface CoordinationPattern {
    id: string;
    name: string;
    limbs: number[];
    pattern: (time: number, limbs: Limb[]) => ControlSignal[];
    complexity: number;
    effectiveness: number;
    lastUsed: number;
}

export interface ExplorationTarget {
    x: number;
    y: number;
    z: number;
    intensity: number; // How interesting this target is
    discoveredAt: number;
    touchedAt?: number;
    nature: 'unexplored' | 'interesting' | 'contact' | 'energy_source';
}

export interface LimbExplorationState {
    limbId: number;
    curiosity: number; // 0-1, how curious this limb is
    currentTarget?: ExplorationTarget;
    explorationHistory: ExplorationTarget[];
    lastExplorationTime: number;
    discoveryCount: number;
    phase: 'reaching' | 'probing' | 'retreating' | 'resting';
    phaseStartTime: number;
}

export interface NeuralControlState {
    activeSignals: Map<number, ControlSignal>;
    coordinationPatterns: Map<string, CoordinationPattern>;
    controlNodes: Map<number, number[]>; // nodeId -> limbIds
    learningRate: number;
    adaptationFactor: number;
    coordinationLevel: number;
    explorationStates: Map<number, LimbExplorationState>;
    curiosityCycles: Map<number, number>; // limbId -> cycle progress (0-1)
}

export class LimbControlSystem {
    private network: NeuralNetworkSimulator;
    private mathEngine: LifeBehaviorMathEngine; // Mathematical models for life behaviors
    private controlState: NeuralControlState;
    private time: number = 0;
    
    // Chaos theory for exploration - Lorenz attractors for unpredictable but structured exploration
    private explorationAttractors: Map<number, AttractorState> = new Map();
    
    // Control parameters
    private readonly MAX_CONTROL_SIGNALS = 10;
    private readonly SIGNAL_DECAY_RATE = 0.95;
    private readonly LEARNING_RATE = 0.01;
    private readonly ADAPTATION_RATE = 0.005;
    
    // Curiosity and exploration parameters
    private readonly EXPLORATION_RADIUS = 250; // Even farther reach
    private readonly CURIOSITY_GROWTH_RATE = 0.003; // Faster curiosity growth
    private readonly DISCOVERY_BOOST = 0.2; // Bigger discovery boost
    private readonly PHASE_DURATION = 2.0; // Faster phases - more energetic
    
    constructor(network: NeuralNetworkSimulator) {
        this.network = network;
        this.mathEngine = new LifeBehaviorMathEngine(); // Initialize mathematical models
        this.controlState = this.initializeControlState();
        this.setupCoordinationPatterns();
    }
    
    private initializeControlState(): NeuralControlState {
        return {
            activeSignals: new Map(),
            coordinationPatterns: new Map(),
            controlNodes: new Map(),
            learningRate: this.LEARNING_RATE,
            adaptationFactor: this.ADAPTATION_RATE,
            coordinationLevel: 0.5,
            explorationStates: new Map(),
            curiosityCycles: new Map()
        };
    }
    
    private setupCoordinationPatterns(): void {
        // Basic movement patterns
        this.addCoordinationPattern({
            id: 'exploration',
            name: 'Exploration Pattern',
            limbs: [],
            pattern: this.createExplorationPattern(),
            complexity: 1,
            effectiveness: 0.7,
            lastUsed: 0
        });
        
        this.addCoordinationPattern({
            id: 'defensive',
            name: 'Defensive Pattern',
            limbs: [],
            pattern: this.createDefensivePattern(),
            complexity: 2,
            effectiveness: 0.8,
            lastUsed: 0
        });
        
        this.addCoordinationPattern({
            id: 'energy_gathering',
            name: 'Energy Gathering Pattern',
            limbs: [],
            pattern: this.createEnergyGatheringPattern(),
            complexity: 3,
            effectiveness: 0.9,
            lastUsed: 0
        });
        
        this.addCoordinationPattern({
            id: 'communication',
            name: 'Communication Pattern',
            limbs: [],
            pattern: this.createCommunicationPattern(),
            complexity: 4,
            effectiveness: 0.6,
            lastUsed: 0
        });
        
        this.addCoordinationPattern({
            id: 'fractal_growth',
            name: 'Fractal Growth Pattern',
            limbs: [],
            pattern: this.createFractalGrowthPattern(),
            complexity: 5,
            effectiveness: 0.85,
            lastUsed: 0
        });
    }
    
    // Main control update method
    public updateLimbControl(limbs: Limb[]): void {
        this.time = this.network.step / 60;
        
        // Initialize exploration states for new limbs
        this.initializeExplorationStates(limbs);
        
        // Update curiosity-driven exploration
        this.updateCuriosityDrivenExploration(limbs);
        
        // Update active control signals
        this.updateActiveSignals();
        
        // Generate new control signals based on neural activity
        this.generateControlSignals(limbs);
        
        // Apply coordination patterns
        this.applyCoordinationPatterns(limbs);
        
        // Update learning and adaptation
        this.updateLearningAndAdaptation(limbs);
        
        // Clean up expired signals
        this.cleanupExpiredSignals();
    }
    
    private initializeExplorationStates(limbs: Limb[]): void {
        for (const limb of limbs) {
            if (!this.controlState.explorationStates.has(limb.id)) {
                const explorationState: LimbExplorationState = {
                    limbId: limb.id,
                    curiosity: 0.5 + Math.random() * 0.4, // Start highly curious!
                    currentTarget: undefined,
                    explorationHistory: [],
                    lastExplorationTime: 0,
                    discoveryCount: 0,
                    phase: 'reaching', // Start exploring immediately
                    phaseStartTime: this.time
                };
                this.controlState.explorationStates.set(limb.id, explorationState);
                this.controlState.curiosityCycles.set(limb.id, 0);
            }
        }
    }
    
    private updateCuriosityDrivenExploration(limbs: Limb[]): void {
        for (const limb of limbs) {
            const explorationState = this.controlState.explorationStates.get(limb.id);
            if (!explorationState) continue;
            
            // Grow curiosity over time
            explorationState.curiosity = Math.min(1, 
                explorationState.curiosity + this.CURIOSITY_GROWTH_RATE * 
                (this.network.linguisticComplexity * 0.1 + 1)
            );
            
            // Update curiosity cycle
            let cycleProgress = this.controlState.curiosityCycles.get(limb.id) || 0;
            cycleProgress += 0.001 * explorationState.curiosity;
            if (cycleProgress >= 1) cycleProgress = 0;
            this.controlState.curiosityCycles.set(limb.id, cycleProgress);
            
            // Decide what to do based on curiosity and phase
            this.updateLimbExplorationPhase(limb, explorationState);
        }
    }
    
    private updateLimbExplorationPhase(limb: Limb, state: LimbExplorationState): void {
        const phaseDuration = this.time - state.phaseStartTime;
        const rootNode = this.network.nodes.find(n => n.id === limb.rootNodeId);
        if (!rootNode) return;
        
        // Check if it's time to transition phases
        if (phaseDuration > this.PHASE_DURATION) {
            this.transitionToNextPhase(limb, state);
            return;
        }
        
        // Execute current phase behavior
        switch (state.phase) {
            case 'resting':
                // Rest and recover, then become curious
                if (state.curiosity > 0.5 && Math.random() < 0.3) {
                    state.phase = 'reaching';
                    state.phaseStartTime = this.time;
                }
                break;
                
            case 'reaching':
                // Reach out toward an exploration target
                this.executeReachingPhase(limb, state, rootNode);
                break;
                
            case 'probing':
                // Feel around at the target
                this.executeProbingPhase(limb, state, rootNode);
                break;
                
            case 'retreating':
                // Bring information back
                this.executeRetreatingPhase(limb, state, rootNode);
                break;
        }
    }
    
    private transitionToNextPhase(limb: Limb, state: LimbExplorationState): void {
        switch (state.phase) {
            case 'reaching':
                state.phase = 'probing';
                state.phaseStartTime = this.time;
                state.currentTarget!.touchedAt = this.time;
                console.log(`[Curiosity] ${limb.type} limb ${limb.id} reached target, now probing...`);
                break;
                
            case 'probing':
                state.phase = 'retreating';
                state.phaseStartTime = this.time;
                state.discoveryCount++;
                state.curiosity = Math.min(1, state.curiosity + this.DISCOVERY_BOOST);
                console.log(`[Curiosity] ${limb.type} limb ${limb.id} discovered something!`);
                break;
                
            case 'retreating':
                state.phase = 'resting';
                state.phaseStartTime = this.time;
                state.currentTarget = undefined;
                break;
                
            case 'resting':
                // Maybe start a new exploration cycle
                if (state.curiosity > 0.4 && Math.random() < 0.5) {
                    state.phase = 'reaching';
                    state.phaseStartTime = this.time;
                }
                break;
        }
    }
    
    private executeReachingPhase(limb: Limb, state: LimbExplorationState, rootNode: Node): void {
        // Generate a target if we don't have one
        if (!state.currentTarget) {
            state.currentTarget = this.generateExplorationTarget(rootNode, limb, state);
        }
        
        if (state.currentTarget) {
            // Create control signal to reach toward target with high intensity
            const signal: ControlSignal = {
                limbId: limb.id,
                targetX: state.currentTarget.x,
                targetY: state.currentTarget.y,
                targetZ: state.currentTarget.z,
                intensity: 0.95, // High intensity for exploration
                priority: 0.95, // High priority for curiosity-driven exploration
                duration: Math.floor(this.PHASE_DURATION * 60),
                timestamp: this.time
            };
            
            // Override any existing signals - exploration is more important
            this.controlState.activeSignals.set(limb.id, signal);
        }
    }
    
    private executeProbingPhase(limb: Limb, state: LimbExplorationState, rootNode: Node): void {
        if (!state.currentTarget) return;
        
        // Add small random movements around the target to "feel"
        const time = this.time;
        const probeOffset = 10;
        const target = state.currentTarget;
        
        const signal: ControlSignal = {
            limbId: limb.id,
            targetX: target.x + Math.sin(time * 2) * probeOffset,
            targetY: target.y + Math.cos(time * 2) * probeOffset,
            targetZ: target.z + Math.sin(time * 1.5) * probeOffset,
            intensity: 0.6,
            priority: 0.8,
            duration: Math.floor(this.PHASE_DURATION * 60),
            timestamp: this.time
        };
        
        this.controlState.activeSignals.set(limb.id, signal);
        
        // Detect if there's something interesting here
        this.detectInterestingFeatures(limb, state, target);
    }
    
    private executeRetreatingPhase(limb: Limb, state: LimbExplorationState, rootNode: Node): void {
        // Move back toward the root node
        const signal: ControlSignal = {
            limbId: limb.id,
            targetX: rootNode.x,
            targetY: rootNode.y,
            targetZ: rootNode.z || 0,
            intensity: 0.7,
            priority: 0.7,
            duration: Math.floor(this.PHASE_DURATION * 60),
            timestamp: this.time
        };
        
        this.controlState.activeSignals.set(limb.id, signal);
    }
    
    private generateExplorationTarget(rootNode: Node, limb: Limb, state: LimbExplorationState): ExplorationTarget {
        // MATHEMATICAL MODEL: Use Lorenz attractor for chaos-based but structured exploration
        // Initialize or get existing attractor for this limb
        if (!this.explorationAttractors.has(limb.id)) {
            this.explorationAttractors.set(limb.id, {
                x: Math.random() * 2 - 1,
                y: Math.random() * 2 - 1,
                attractorType: 'strange',
                parameters: { z: Math.random() * 2 - 1 }
            });
        }
        
        // Calculate Lorenz attractor dynamics for unpredictable but structured exploration
        const dt = 0.016; // 60fps timestep
        const attractorState = this.explorationAttractors.get(limb.id)!;
        const newAttractorState = this.mathEngine.attractorModel.calculateLorenzAttractor(attractorState, dt);
        this.explorationAttractors.set(limb.id, newAttractorState);
        
        // Use Lorenz attractor coordinates to generate exploration target
        // This creates unpredictable but bounded exploration patterns
        const chaosScale = state.curiosity * this.EXPLORATION_RADIUS * 0.5;
        const angle = Math.atan2(newAttractorState.y, newAttractorState.x) + Math.random() * 0.5;
        const elevation = Math.asin(Math.max(-0.9, Math.min(0.9, newAttractorState.parameters.z)));
        const distance = 50 + Math.abs(newAttractorState.x * newAttractorState.y) * this.EXPLORATION_RADIUS * state.curiosity;
        
        const x = rootNode.x + Math.cos(angle) * Math.cos(elevation) * distance;
        const y = rootNode.y + Math.sin(angle) * Math.cos(elevation) * distance;
        const z = (rootNode.z || 0) + Math.sin(elevation) * distance;
        
        // Check for interesting features nearby
        const intensity = this.calculateTargetIntensity(x, y, z);
        
        return {
            x, y, z,
            intensity,
            discoveredAt: this.time,
            nature: intensity > 0.5 ? 'interesting' : 'unexplored'
        };
    }
    
    private calculateTargetIntensity(x: number, y: number, z: number): number {
        let intensity = 0.3; // Base curiosity
        
        // Check proximity to other nodes (interesting social structure)
        for (const node of this.network.nodes) {
            const dist = Math.sqrt(
                Math.pow(node.x - x, 2) + 
                Math.pow(node.y - y, 2) + 
                Math.pow((node.z || 0) - z, 2)
            );
            if (dist < 50) {
                intensity += 0.2 * (1 - dist / 50);
            }
        }
        
        // Check proximity to psychonaut creature
        const dx = this.network.psychonautCreature.x - x;
        const dy = this.network.psychonautCreature.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < this.network.psychonautCreature.influenceRadius) {
            intensity += 0.3 * (1 - dist / this.network.psychonautCreature.influenceRadius);
        }
        
        return Math.min(1, intensity);
    }
    
    private detectInterestingFeatures(limb: Limb, state: LimbExplorationState, target: ExplorationTarget): void {
        // Simple feature detection - in a real implementation, this would be more sophisticated
        if (target.intensity > 0.7 && state.currentTarget) {
            state.currentTarget.nature = 'interesting';
            
            // Boost linguistic complexity based on interesting discoveries
            this.network.linguisticComplexity = Math.min(10, 
                this.network.linguisticComplexity + 0.1
            );
        }
    }
    
    private updateActiveSignals(): void {
        for (const [limbId, signal] of this.controlState.activeSignals) {
            // Decay signal intensity
            signal.intensity *= this.SIGNAL_DECAY_RATE;
            
            // Update duration
            signal.duration -= 1;
            
            // Remove weak or expired signals
            if (signal.intensity < 0.1 || signal.duration <= 0) {
                this.controlState.activeSignals.delete(limbId);
            }
        }
    }
    
    private generateControlSignals(limbs: Limb[]): void {
        for (const limb of limbs) {
            // Find control nodes for this limb
            const controlNodes = this.getControlNodesForLimb(limb);
            
            for (const nodeId of controlNodes) {
                const node = this.network.nodes.find(n => n.id === nodeId);
                if (!node) continue;
                
                // Generate control signal based on neural activity
                const signal = this.generateNeuralControlSignal(limb, node);
                if (signal) {
                    this.controlState.activeSignals.set(limb.id, signal);
                }
            }
        }
    }
    
    private getControlNodesForLimb(limb: Limb): number[] {
        // Get control nodes from the control state
        let controlNodes = this.controlState.controlNodes.get(limb.rootNodeId) || [];
        
        // If no control nodes assigned, use the root node
        if (controlNodes.length === 0) {
            controlNodes = [limb.rootNodeId];
            this.controlState.controlNodes.set(limb.rootNodeId, controlNodes);
        }
        
        return controlNodes;
    }
    
    private generateNeuralControlSignal(limb: Limb, node: Node): ControlSignal | null {
        const neuralActivity = node.energy || 0;
        const linguisticActivity = this.network.linguisticComplexity;
        const psychicActivity = this.calculatePsychicActivity();
        
        // Only generate signals for high-activity nodes
        if (neuralActivity < 0.3) return null;
        
        // Calculate target position based on neural patterns
        const targetPosition = this.calculateTargetPosition(limb, node, neuralActivity);
        
        // Calculate signal intensity based on multiple factors
        const intensity = this.calculateSignalIntensity(neuralActivity, linguisticActivity, psychicActivity);
        
        // Calculate priority based on limb type and current state
        const priority = this.calculateSignalPriority(limb, neuralActivity);
        
        return {
            limbId: limb.id,
            targetX: targetPosition.x,
            targetY: targetPosition.y,
            targetZ: targetPosition.z,
            intensity,
            priority,
            duration: Math.floor(30 + neuralActivity * 20),
            timestamp: this.time
        };
    }
    
    private calculateTargetPosition(limb: Limb, node: Node, neuralActivity: number): { x: number; y: number; z: number } {
        const time = this.time;
        const baseX = node.x;
        const baseY = node.y;
        const baseZ = node.z || 0;
        
        // Calculate movement based on limb type
        let offsetX = 0, offsetY = 0, offsetZ = 0;
        
        switch (limb.type) {
            case 'fractal_branch':
                offsetX = Math.sin(time * 0.5 + node.id * 0.1) * 20 * neuralActivity;
                offsetY = Math.cos(time * 0.5 + node.id * 0.1) * 20 * neuralActivity;
                offsetZ = Math.sin(time * 0.3 + node.id * 0.2) * 10 * neuralActivity;
                break;
                
            case 'psychic_manifest':
                offsetX = Math.sin(time * 1.2 + node.id * 0.15) * 30 * neuralActivity;
                offsetY = Math.cos(time * 1.2 + node.id * 0.15) * 30 * neuralActivity;
                offsetZ = Math.sin(time * 0.8 + node.id * 0.25) * 15 * neuralActivity;
                break;
                
            case 'wave_resonance':
                offsetX = Math.sin(time * 2 + node.id * 0.2) * 25 * neuralActivity;
                offsetY = Math.cos(time * 2 + node.id * 0.2) * 25 * neuralActivity;
                offsetZ = Math.sin(time * 1.5 + node.id * 0.3) * 12 * neuralActivity;
                break;
                
            case 'energy_conduit':
                offsetX = Math.sin(time * 0.8 + node.id * 0.12) * 15 * neuralActivity;
                offsetY = Math.cos(time * 0.8 + node.id * 0.12) * 15 * neuralActivity;
                offsetZ = Math.sin(time * 0.6 + node.id * 0.18) * 8 * neuralActivity;
                break;
                
            case 'linguistic_appendage':
                offsetX = Math.sin(time * 1.5 + node.id * 0.18) * 18 * neuralActivity;
                offsetY = Math.cos(time * 1.5 + node.id * 0.18) * 18 * neuralActivity;
                offsetZ = Math.sin(time * 1.2 + node.id * 0.22) * 9 * neuralActivity;
                break;
                
            case 'mystical_tendril':
                offsetX = Math.sin(time * 0.6 + node.id * 0.25) * 35 * neuralActivity;
                offsetY = Math.cos(time * 0.6 + node.id * 0.25) * 35 * neuralActivity;
                offsetZ = Math.sin(time * 0.4 + node.id * 0.35) * 20 * neuralActivity;
                break;
                
            case 'temporal_phase':
                offsetX = Math.sin(time * 0.3 + node.id * 0.3) * 40 * neuralActivity;
                offsetY = Math.cos(time * 0.3 + node.id * 0.3) * 40 * neuralActivity;
                offsetZ = Math.sin(time * 0.2 + node.id * 0.4) * 25 * neuralActivity;
                break;
                
            default:
                offsetX = Math.sin(time + node.id * 0.1) * 20 * neuralActivity;
                offsetY = Math.cos(time + node.id * 0.1) * 20 * neuralActivity;
                offsetZ = Math.sin(time * 0.7 + node.id * 0.15) * 10 * neuralActivity;
        }
        
        return {
            x: baseX + offsetX,
            y: baseY + offsetY,
            z: baseZ + offsetZ
        };
    }
    
    private calculateSignalIntensity(neuralActivity: number, linguisticActivity: number, psychicActivity: number): number {
        const baseIntensity = neuralActivity;
        const linguisticBoost = linguisticActivity * 0.1;
        const psychicBoost = psychicActivity * 0.15;
        
        return Math.min(1, baseIntensity + linguisticBoost + psychicBoost);
    }
    
    private calculateSignalPriority(limb: Limb, neuralActivity: number): number {
        let priority = neuralActivity;
        
        // Adjust priority based on limb type
        switch (limb.type) {
            case 'defensive_spike':
                priority *= 1.5; // High priority for defense
                break;
            case 'sensing_probe':
                priority *= 1.3; // High priority for sensing
                break;
            case 'energy_conduit':
                priority *= 1.2; // High priority for energy
                break;
            case 'fractal_branch':
                priority *= 1.1; // Medium priority for exploration
                break;
            case 'linguistic_appendage':
                priority *= 0.9; // Lower priority for communication
                break;
        }
        
        // Adjust priority based on limb state
        if (limb.state === 'active') {
            priority *= 1.2;
        } else if (limb.state === 'dormant') {
            priority *= 0.5;
        }
        
        return Math.min(1, priority);
    }
    
    private calculatePsychicActivity(): number {
        const energyFactor = this.network.totalNetworkEnergy / 1000;
        const linguisticFactor = this.network.linguisticComplexity / 10;
        return (energyFactor + linguisticFactor) / 2;
    }
    
    private applyCoordinationPatterns(limbs: Limb[]): void {
        // Determine which coordination pattern to use
        const activePattern = this.selectCoordinationPattern(limbs);
        if (!activePattern) return;
        
        // Apply the pattern
        const signals = activePattern.pattern(this.time, limbs);
        for (const signal of signals) {
            this.controlState.activeSignals.set(signal.limbId, signal);
        }
        
        // Update pattern usage
        activePattern.lastUsed = this.time;
    }
    
    private selectCoordinationPattern(limbs: Limb[]): CoordinationPattern | null {
        const patterns = Array.from(this.controlState.coordinationPatterns.values());
        
        // Filter patterns based on available limbs
        const availablePatterns = patterns.filter(pattern => {
            return pattern.limbs.length === 0 || 
                   pattern.limbs.some(limbId => limbs.some(limb => limb.id === limbId));
        });
        
        if (availablePatterns.length === 0) return null;
        
        // Select pattern based on creature state and effectiveness
        let bestPattern = availablePatterns[0];
        let bestScore = 0;
        
        for (const pattern of availablePatterns) {
            const score = this.calculatePatternScore(pattern, limbs);
            if (score > bestScore) {
                bestScore = score;
                bestPattern = pattern;
            }
        }
        
        return bestPattern;
    }
    
    private calculatePatternScore(pattern: CoordinationPattern, limbs: Limb[]): number {
        let score = pattern.effectiveness;
        
        // Adjust score based on creature state
        switch (this.network.creatureState) {
            case 'exploring':
                if (pattern.id === 'exploration') score *= 1.5;
                break;
            case 'sensing':
                if (pattern.id === 'defensive') score *= 1.3;
                break;
            case 'stabilizing':
                if (pattern.id === 'energy_gathering') score *= 1.4;
                break;
            case 'growing':
                if (pattern.id === 'fractal_growth') score *= 1.6;
                break;
        }
        
        // Adjust score based on linguistic activity
        if (this.network.linguisticComplexity > 5 && pattern.id === 'communication') {
            score *= 1.3;
        }
        
        // Adjust score based on recency (avoid overusing same pattern)
        const timeSinceLastUse = this.time - pattern.lastUsed;
        if (timeSinceLastUse < 10) {
            score *= 0.7; // Reduce score for recently used patterns
        }
        
        return score;
    }
    
    private updateLearningAndAdaptation(limbs: Limb[]): void {
        // Update learning rate based on success
        const successRate = this.calculateSuccessRate(limbs);
        this.controlState.learningRate = Math.min(0.1, this.controlState.learningRate + successRate * 0.001);
        
        // Update adaptation factor
        this.controlState.adaptationFactor = Math.min(0.01, this.controlState.adaptationFactor + successRate * 0.0005);
        
        // Update coordination level
        this.controlState.coordinationLevel = Math.min(1, this.controlState.coordinationLevel + successRate * 0.002);
    }
    
    private calculateSuccessRate(limbs: Limb[]): number {
        let totalSuccess = 0;
        let totalAttempts = 0;
        
        for (const limb of limbs) {
            totalSuccess += limb.effectiveness;
            totalAttempts += 1;
        }
        
        return totalAttempts > 0 ? totalSuccess / totalAttempts : 0;
    }
    
    private cleanupExpiredSignals(): void {
        const currentTime = this.time;
        for (const [limbId, signal] of this.controlState.activeSignals) {
            if (currentTime - signal.timestamp > signal.duration) {
                this.controlState.activeSignals.delete(limbId);
            }
        }
    }
    
    // Coordination pattern creators
    private createExplorationPattern(): (time: number, limbs: Limb[]) => ControlSignal[] {
        return (time: number, limbs: Limb[]) => {
            const signals: ControlSignal[] = [];
            
            for (const limb of limbs) {
                if (limb.type === 'fractal_branch' || limb.type === 'sensing_probe') {
                    const angle = time * 0.3 + limb.id * 0.5;
                    const radius = 50 + Math.sin(time * 0.2) * 20;
                    
                    signals.push({
                        limbId: limb.id,
                        targetX: limb.segments[0]?.x + Math.cos(angle) * radius || 0,
                        targetY: limb.segments[0]?.y + Math.sin(angle) * radius || 0,
                        targetZ: limb.segments[0]?.z + Math.sin(angle * 1.3) * 10 || 0,
                        intensity: 0.7,
                        priority: 0.8,
                        duration: 60,
                        timestamp: time
                    });
                }
            }
            
            return signals;
        };
    }
    
    private createDefensivePattern(): (time: number, limbs: Limb[]) => ControlSignal[] {
        return (time: number, limbs: Limb[]) => {
            const signals: ControlSignal[] = [];
            
            for (const limb of limbs) {
                if (limb.type === 'defensive_spike' || limb.type === 'mystical_tendril') {
                    // Defensive pattern: rapid, sharp movements
                    const angle = time * 2 + limb.id * 0.8;
                    const radius = 30 + Math.sin(time * 4) * 15;
                    
                    signals.push({
                        limbId: limb.id,
                        targetX: limb.segments[0]?.x + Math.cos(angle) * radius || 0,
                        targetY: limb.segments[0]?.y + Math.sin(angle) * radius || 0,
                        targetZ: limb.segments[0]?.z + Math.sin(angle * 2) * 8 || 0,
                        intensity: 0.9,
                        priority: 1.0,
                        duration: 30,
                        timestamp: time
                    });
                }
            }
            
            return signals;
        };
    }
    
    private createEnergyGatheringPattern(): (time: number, limbs: Limb[]) => ControlSignal[] {
        return (time: number, limbs: Limb[]) => {
            const signals: ControlSignal[] = [];
            
            for (const limb of limbs) {
                if (limb.type === 'energy_conduit' || limb.type === 'psychic_manifest') {
                    // Energy gathering: slow, deliberate movements toward energy sources
                    const angle = time * 0.1 + limb.id * 0.3;
                    const radius = 40 + Math.cos(time * 0.5) * 10;
                    
                    signals.push({
                        limbId: limb.id,
                        targetX: limb.segments[0]?.x + Math.cos(angle) * radius || 0,
                        targetY: limb.segments[0]?.y + Math.sin(angle) * radius || 0,
                        targetZ: limb.segments[0]?.z + Math.cos(angle * 0.7) * 5 || 0,
                        intensity: 0.8,
                        priority: 0.9,
                        duration: 90,
                        timestamp: time
                    });
                }
            }
            
            return signals;
        };
    }
    
    private createCommunicationPattern(): (time: number, limbs: Limb[]) => ControlSignal[] {
        return (time: number, limbs: Limb[]) => {
            const signals: ControlSignal[] = [];
            
            for (const limb of limbs) {
                if (limb.type === 'linguistic_appendage' || limb.type === 'vocal_modifier') {
                    // Communication pattern: rhythmic, expressive movements
                    const angle = time * 1.5 + limb.id * 0.4;
                    const radius = 25 + Math.sin(time * 3) * 8;
                    
                    signals.push({
                        limbId: limb.id,
                        targetX: limb.segments[0]?.x + Math.cos(angle) * radius || 0,
                        targetY: limb.segments[0]?.y + Math.sin(angle) * radius || 0,
                        targetZ: limb.segments[0]?.z + Math.sin(angle * 1.5) * 6 || 0,
                        intensity: 0.6,
                        priority: 0.7,
                        duration: 45,
                        timestamp: time
                    });
                }
            }
            
            return signals;
        };
    }
    
    private createFractalGrowthPattern(): (time: number, limbs: Limb[]) => ControlSignal[] {
        return (time: number, limbs: Limb[]) => {
            const signals: ControlSignal[] = [];
            
            for (const limb of limbs) {
                if (limb.type === 'fractal_branch' || limb.type === 'neural_extension') {
                    // Fractal growth: complex, self-similar movements
                    const goldenRatio = 1.618;
                    const angle = time * 0.2 + limb.id * goldenRatio;
                    const radius = 60 + Math.sin(time * 0.3 + limb.id * 0.1) * 25;
                    
                    signals.push({
                        limbId: limb.id,
                        targetX: limb.segments[0]?.x + Math.cos(angle) * radius || 0,
                        targetY: limb.segments[0]?.y + Math.sin(angle) * radius || 0,
                        targetZ: limb.segments[0]?.z + Math.sin(angle * goldenRatio) * 12 || 0,
                        intensity: 0.85,
                        priority: 0.9,
                        duration: 120,
                        timestamp: time
                    });
                }
            }
            
            return signals;
        };
    }
    
    // Public interface methods
    public addCoordinationPattern(pattern: CoordinationPattern): void {
        this.controlState.coordinationPatterns.set(pattern.id, pattern);
    }
    
    public removeCoordinationPattern(patternId: string): boolean {
        return this.controlState.coordinationPatterns.delete(patternId);
    }
    
    public getActiveSignals(): ControlSignal[] {
        return Array.from(this.controlState.activeSignals.values());
    }
    
    public getCoordinationPatterns(): CoordinationPattern[] {
        return Array.from(this.controlState.coordinationPatterns.values());
    }
    
    public getControlState(): NeuralControlState {
        return { ...this.controlState };
    }
    
    public setLearningRate(rate: number): void {
        this.controlState.learningRate = Math.max(0, Math.min(0.1, rate));
    }
    
    public setCoordinationLevel(level: number): void {
        this.controlState.coordinationLevel = Math.max(0, Math.min(1, level));
    }
    
    // Curiosity and exploration interface methods
    public getExplorationState(limbId: number): LimbExplorationState | undefined {
        return this.controlState.explorationStates.get(limbId);
    }
    
    public getAllExplorationStates(): Map<number, LimbExplorationState> {
        return this.controlState.explorationStates;
    }
    
    public getCuriosityCycles(): Map<number, number> {
        return this.controlState.curiosityCycles;
    }
    
    public getAverageCuriosity(): number {
        const states = Array.from(this.controlState.explorationStates.values());
        if (states.length === 0) return 0;
        return states.reduce((sum, state) => sum + state.curiosity, 0) / states.length;
    }
    
    public getTotalDiscoveries(): number {
        return Array.from(this.controlState.explorationStates.values())
            .reduce((sum, state) => sum + state.discoveryCount, 0);
    }
    
    public boostCuriosity(limbId: number, amount: number): void {
        const state = this.controlState.explorationStates.get(limbId);
        if (state) {
            state.curiosity = Math.min(1, state.curiosity + amount);
        }
    }
}
